import { useState } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';

// ─── Default shapes ───
const SIMPLE_FIELDS = [
  { key: 'height', label: 'Height' },
  { key: 'width', label: 'Width' },
  { key: 'depth', label: 'Depth' },
  { key: 'seat_height', label: 'Seat height' },
  { key: 'arm_height', label: 'Arm height' },
  { key: 'stack_height', label: 'Stack height' },
  { key: 'lowest_height', label: 'Lowest height' },
  { key: 'highest_height', label: 'Highest height' },
  { key: 'weight', label: 'Weight', isWeight: true },
];

const TABLE_BASE_FIELDS = [
  { kind: 'pair', keys: ['base_size_length', 'base_size_depth'], label: 'Base size' },
  { key: 'spider_size', label: 'Spider size' },
  { key: 'column_width', label: 'Column size' },
  { key: 'height', label: 'Height' },
  { key: 'height_2', label: 'Height 2' },
  { key: 'weight', label: 'Weight', isWeight: true },
];

const MAX_TOP_FIELDS = [
  { key: 'max_top_round', label: 'Max top round' },
  { key: 'max_top_square', label: 'Max top square' },
  { kind: 'pair', keys: ['max_top_rectangle', 'max_top_rectangle_2'], label: 'Max top rectangle' },
];

// 1 yard of fabric (COM) is treated as 18 square feet of leather (COL).
// In metric: 1 m × 18 sq ft/yd × 0.09290304 sq mtr/sq ft ÷ 0.9144 yd/m ≈ 1.8288.
const COM_TO_COL_MULTIPLIER = { imperial: 18, metric: 1.8288 };

const defaultDims = () => ({
  activeTab: 'simple',
  inputUnit: 'imperial',
  simple: { sectionTitle: 'Dimensions', fabricSectionTitle: 'Fabric Consumption', com: '', col: '', fields: {} },
  advanced: {
    sectionTitle: 'Dimensions',
    fabricSectionTitle: 'Fabric Consumption',
    globalFields: {},
    globalCom: '',
    globalCol: '',
    variants: [],
  },
  tableTops: {
    title: 'Size Table',
    mode: 'single',
    weightTitles: [],
    rows: [],
  },
  tableBases: {
    title: 'Table base',
    categories: [],
    variants: [],
  },
});

// ─── Pill toggle button group (separated, rounded) ───
const PillToggle = ({ value, options, onChange }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <Button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          disableElevation
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 99,
            border: '1px solid',
            borderColor: active ? 'text.primary' : 'divider',
            color: 'text.primary',
            bgcolor: 'background.paper',
            textTransform: 'none',
            fontWeight: active ? 600 : 500,
            fontSize: '0.9rem',
            minWidth: 'auto',
            '&:hover': { bgcolor: 'action.hover', borderColor: 'text.primary' },
          }}
        >
          {opt.label}
        </Button>
      );
    })}
  </Box>
);

// ─── Big editable title with pencil affordance ───
const BigTitleInput = ({ value, onChange }) => (
  <TextField
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    size="small"
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <EditRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
        </InputAdornment>
      ),
      sx: {
        borderRadius: 1.5,
        fontSize: '0.95rem',
        fontWeight: 600,
        py: 0,
      },
    }}
  />
);

// ─── Field with unit suffix on the right (outside the input) ───
const UnitField = ({ label, value, onChange, unit, placeholder = '—' }) => (
  <Box>
    <Typography sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        fullWidth
        InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.8rem', py: 0 } }}
        inputProps={{ style: { padding: '6px 8px' } }}
      />
      <Typography sx={{ minWidth: 22, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
        {unit}
      </Typography>
    </Box>
  </Box>
);

// Two-input variant for fields with a natural pair of values (e.g. base size
// length × depth, rectangle width × depth). Inputs share one label.
const PairUnitField = ({ label, values, onChange, unit }) => (
  <Box>
    <Typography sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TextField
        size="small"
        type="number"
        value={values[0] ?? ''}
        onChange={(e) => onChange(0, e.target.value)}
        placeholder="—"
        fullWidth
        InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.8rem', py: 0 } }}
        inputProps={{ style: { padding: '6px 8px' } }}
      />
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>×</Typography>
      <TextField
        size="small"
        type="number"
        value={values[1] ?? ''}
        onChange={(e) => onChange(1, e.target.value)}
        placeholder="—"
        fullWidth
        InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.8rem', py: 0 } }}
        inputProps={{ style: { padding: '6px 8px' } }}
      />
      <Typography sx={{ minWidth: 22, fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
        {unit}
      </Typography>
    </Box>
  </Box>
);

// ─── Uppercase section label ───
const SectionLabel = ({ children, sx }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      textTransform: 'uppercase',
      fontWeight: 700,
      letterSpacing: '0.05em',
      color: 'text.secondary',
      mb: 1,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const DimensionsEditor = ({ value, onChange, productSlug = '' }) => {
  const def = defaultDims();
  const dims = {
    ...def,
    ...(value || {}),
    simple: { ...def.simple, ...(value?.simple || {}), fields: { ...(value?.simple?.fields || {}) } },
    advanced: { ...def.advanced, ...(value?.advanced || {}) },
    tableTops: { ...def.tableTops, ...(value?.tableTops || {}) },
    tableBases: { ...def.tableBases, ...(value?.tableBases || {}) },
  };

  const update = (patch) => onChange({ ...dims, ...patch });
  const updateSimple = (patch) => update({ simple: { ...dims.simple, ...patch } });
  const updateSimpleField = (key, val) => updateSimple({ fields: { ...dims.simple.fields, [key]: val } });
  const updateAdvanced = (patch) => update({ advanced: { ...dims.advanced, ...patch } });
  const updateTableTops = (patch) => update({ tableTops: { ...dims.tableTops, ...patch } });
  const updateTableBases = (patch) => update({ tableBases: { ...dims.tableBases, ...patch } });

  // ─── Shared drag-to-reorder helpers (native HTML5 DnD) ───
  // `list` scopes a drag so dragging one list can't drop into another.
  const [drag, setDrag] = useState({ list: null, from: null, over: null });
  const dragHandleProps = (list, idx) => ({
    draggable: true,
    onDragStart: (e) => {
      setDrag({ list, from: idx, over: idx });
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* Safari */ }
    },
    onDragEnd: () => setDrag({ list: null, from: null, over: null }),
  });
  const dropZoneProps = (list, idx, arr, apply) => ({
    onDragOver: (e) => { e.preventDefault(); if (!(drag.list === list && drag.over === idx)) setDrag((s) => ({ ...s, over: idx })); },
    onDrop: (e) => {
      e.preventDefault();
      if (drag.list === list && drag.from !== null && drag.from !== idx) {
        const next = [...arr];
        const [m] = next.splice(drag.from, 1);
        next.splice(idx, 0, m);
        apply(next);
      }
      setDrag({ list: null, from: null, over: null });
    },
  });
  const isDragOver = (list, idx) => drag.list === list && drag.over === idx && drag.from !== null && drag.from !== idx;
  const isDragging = (list, idx) => drag.list === list && drag.from === idx;
  // Move an item up/down by one — used where a drag handle doesn't fit (grid rows).
  const moveInArray = (arr, from, to) => {
    if (to < 0 || to >= arr.length) return arr;
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  };

  const lengthUnit = dims.inputUnit === 'imperial' ? 'in' : 'cm';
  const weightUnit = dims.inputUnit === 'imperial' ? 'lbs' : 'kg';
  const fabricUnit = dims.inputUnit === 'imperial' ? 'yd' : 'm';
  const inputUnitLabel = dims.inputUnit === 'imperial' ? 'imperial' : 'metric';
  const colUnitForCurrentMode = dims.inputUnit === 'imperial' ? 'sq ft' : 'sq mtr';
  const comToColMultiplier = COM_TO_COL_MULTIPLIER[dims.inputUnit] || COM_TO_COL_MULTIPLIER.imperial;
  const round3 = (n) => Math.round(n * 1000) / 1000;
  const comToCol = (com) => {
    const n = parseFloat(com);
    return Number.isFinite(n) ? String(round3(n * comToColMultiplier)) : '';
  };
  const colToCom = (col) => {
    const n = parseFloat(col);
    return Number.isFinite(n) ? String(round3(n / comToColMultiplier)) : '';
  };

  const setUnit = (unit) => update({ inputUnit: unit });
  const setTab = (tab) => update({ activeTab: tab });

  // ─── Per-tab header (slug + "— input in imperial/metric") ───
  const TabHeader = ({ subtitle, rightToggles }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
          {productSlug || 'product'}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: 'text.secondary' }}>
          {subtitle} — input in {inputUnitLabel}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {rightToggles}
        <PillToggle
          value={dims.inputUnit}
          options={[{ value: 'imperial', label: 'Imperial' }, { value: 'metric', label: 'Metric' }]}
          onChange={setUnit}
        />
      </Box>
    </Box>
  );

  // ─── Simple tab ───
  const renderSimple = () => (
    <Box>
      <TabHeader subtitle="Single dimension set, no variants" />

      <BigTitleInput value={dims.simple.sectionTitle} onChange={(v) => updateSimple({ sectionTitle: v })} />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1.5, mb: 2 }}>
        {SIMPLE_FIELDS.map((f) => (
          <UnitField
            key={f.key}
            label={f.label}
            value={dims.simple.fields[f.key]}
            onChange={(v) => updateSimpleField(f.key, v)}
            unit={f.isWeight ? weightUnit : lengthUnit}
          />
        ))}
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
        <BigTitleInput value={dims.simple.fabricSectionTitle} onChange={(v) => updateSimple({ fabricSectionTitle: v })} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mt: 1.5, maxWidth: 480 }}>
          <UnitField
            label={`COM (${fabricUnit === 'yd' ? 'yards' : 'meters'})`}
            value={dims.simple.com}
            onChange={(v) => updateSimple({ com: v, col: comToCol(v) })}
            unit={fabricUnit === 'yd' ? 'yd' : 'm'}
          />
          <UnitField
            label={`COL (${colUnitForCurrentMode})`}
            value={dims.simple.col}
            onChange={(v) => updateSimple({ col: v, com: colToCom(v) })}
            unit={colUnitForCurrentMode}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          1 yard fabric ≈ 18 sq ft leather. Editing either field auto-fills the other; override manually if needed.
        </Typography>
      </Box>
    </Box>
  );

  // ─── Advanced tab — Global dimensions + Variants (each can override) ───
  const adv = dims.advanced;
  const advVariants = adv.variants || [];
  const updateGlobalField = (key, val) =>
    updateAdvanced({ globalFields: { ...adv.globalFields, [key]: val } });
  const addAdvVariant = () =>
    updateAdvanced({ variants: [...advVariants, { name: '', fields: {}, com: '', col: '' }] });
  const updateAdvVariant = (idx, patch) =>
    updateAdvanced({ variants: advVariants.map((v, i) => (i === idx ? { ...v, ...patch } : v)) });
  const removeAdvVariant = (idx) => updateAdvanced({ variants: advVariants.filter((_, i) => i !== idx) });

  const colUnit = colUnitForCurrentMode;

  const renderAdvanced = () => (
    <Box>
      <TabHeader subtitle="Global dimensions + variant-specific overrides" />

      {/* ── Global section ── */}
      <BigTitleInput
        value={adv.sectionTitle || 'Dimensions'}
        onChange={(v) => updateAdvanced({ sectionTitle: v })}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1.5, mb: 2 }}>
        {SIMPLE_FIELDS.map((f) => (
          <UnitField
            key={f.key}
            label={f.label}
            value={adv.globalFields?.[f.key]}
            onChange={(v) => updateGlobalField(f.key, v)}
            unit={f.isWeight ? weightUnit : lengthUnit}
          />
        ))}
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, mb: 2 }}>
        <BigTitleInput
          value={adv.fabricSectionTitle || 'Fabric Consumption'}
          onChange={(v) => updateAdvanced({ fabricSectionTitle: v })}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mt: 1.5, maxWidth: 480 }}>
          <UnitField
            label={`COM (${fabricUnit === 'yd' ? 'yards' : 'meters'})`}
            value={adv.globalCom}
            onChange={(v) => updateAdvanced({ globalCom: v, globalCol: comToCol(v) })}
            unit={fabricUnit === 'yd' ? 'yd' : 'm'}
          />
          <UnitField
            label={`COL (${colUnit})`}
            value={adv.globalCol}
            onChange={(v) => updateAdvanced({ globalCol: v, globalCom: colToCom(v) })}
            unit={colUnit}
          />
        </Box>
      </Box>

      {/* ── Variants ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
        <SectionLabel sx={{ mb: 0 }}>Variants</SectionLabel>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded sx={{ fontSize: 16 }} />}
          onClick={addAdvVariant}
          sx={{ borderRadius: 99, textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
        >
          Add Variant
        </Button>
      </Box>

      {advVariants.length === 0 && (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          No variants yet. Variant fields override the global ones above.
        </Typography>
      )}

      {advVariants.map((v, idx) => (
        <Box
          key={idx}
          {...dropZoneProps('advVariants', idx, advVariants, (next) => updateAdvanced({ variants: next }))}
          sx={{
            mb: 1.5, border: '1px solid',
            borderColor: isDragOver('advVariants', idx) ? 'primary.main' : 'divider',
            borderRadius: 1.5, p: 1.5, opacity: isDragging('advVariants', idx) ? 0.5 : 1,
            transition: 'border-color 0.12s, opacity 0.12s',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
            <Box
              {...dragHandleProps('advVariants', idx)}
              sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
              title="Drag to reorder"
            >
              <DragIndicatorRounded sx={{ fontSize: 18 }} />
            </Box>
            <TextField
              size="small"
              placeholder="Variant name (e.g. Full Back)"
              value={v.name}
              onChange={(e) => updateAdvVariant(idx, { name: e.target.value })}
              fullWidth
              InputProps={{ sx: { borderRadius: 1.5, fontSize: '0.85rem', fontWeight: 600 } }}
              inputProps={{ style: { padding: '6px 8px' } }}
            />
            <IconButton
              size="small"
              onClick={() => removeAdvVariant(idx)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
            Dimensions (override)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 1.5 }}>
            {SIMPLE_FIELDS.map((f) => (
              <UnitField
                key={f.key}
                label={f.label}
                value={v.fields?.[f.key]}
                onChange={(val) => updateAdvVariant(idx, { fields: { ...v.fields, [f.key]: val } })}
                unit={f.isWeight ? weightUnit : lengthUnit}
              />
            ))}
          </Box>

          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
            Fabric Consumption
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, maxWidth: 480 }}>
            <UnitField
              label={`COM (${fabricUnit === 'yd' ? 'yards' : 'meters'})`}
              value={v.com}
              onChange={(val) => updateAdvVariant(idx, { com: val, col: comToCol(val) })}
              unit={fabricUnit === 'yd' ? 'yd' : 'm'}
            />
            <UnitField
              label={`COL (${colUnit})`}
              value={v.col}
              onChange={(val) => updateAdvVariant(idx, { col: val, com: colToCom(val) })}
              unit={colUnit}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );

  // ─── Table Tops tab ───
  const tt = dims.tableTops;
  const ttRows = tt.rows || [];
  const ttWeightTitles = tt.weightTitles || [];

  const setTtMode = (mode) => {
    if (mode === 'single') {
      updateTableTops({
        mode,
        weightTitles: [],
        rows: ttRows.map((r) => ({ ...r, weights: [{ value: r.weights?.[0]?.value ?? '' }] })),
      });
    } else {
      const titles = ttWeightTitles.length > 0 ? ttWeightTitles : ['Thickness 1'];
      updateTableTops({
        mode,
        weightTitles: titles,
        rows: ttRows.map((r) => ({
          ...r,
          weights: titles.map((t, i) => ({ title: t, value: r.weights?.[i]?.value ?? '' })),
        })),
      });
    }
  };

  const addWeightCol = () => {
    const titles = [...ttWeightTitles, `Thickness ${ttWeightTitles.length + 1}`];
    updateTableTops({
      weightTitles: titles,
      rows: ttRows.map((r) => ({
        ...r,
        weights: [...(r.weights || []), { title: titles[titles.length - 1], value: '' }],
      })),
    });
  };
  const removeWeightCol = (i) => {
    const titles = ttWeightTitles.filter((_, idx) => idx !== i);
    updateTableTops({
      weightTitles: titles,
      rows: ttRows.map((r) => ({ ...r, weights: (r.weights || []).filter((_, idx) => idx !== i) })),
    });
  };
  const renameWeightCol = (i, name) => {
    const titles = ttWeightTitles.map((t, idx) => (idx === i ? name : t));
    updateTableTops({
      weightTitles: titles,
      rows: ttRows.map((r) => ({
        ...r,
        weights: (r.weights || []).map((w, idx) => (idx === i ? { ...w, title: name } : w)),
      })),
    });
  };

  const addTtRow = () => {
    const weights = tt.mode === 'multi'
      ? ttWeightTitles.map((t) => ({ title: t, value: '' }))
      : [{ value: '' }];
    updateTableTops({ rows: [...ttRows, { size: '', shape: '', sizeOther: '', sizeExtended: '', weights }] });
  };
  const removeTtRow = (idx) => updateTableTops({ rows: ttRows.filter((_, i) => i !== idx) });
  const updateTtRow = (idx, patch) =>
    updateTableTops({ rows: ttRows.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const updateTtRowWeight = (rowIdx, weightIdx, val) =>
    updateTableTops({
      rows: ttRows.map((r, i) =>
        i === rowIdx
          ? { ...r, weights: (r.weights || []).map((w, wi) => (wi === weightIdx ? { ...w, value: val } : w)) }
          : r
      ),
    });

  const numWeightCols = tt.mode === 'multi' ? Math.max(ttWeightTitles.length, 1) : 1;
  const ttGridCols = `100px 130px 110px 130px ${'130px '.repeat(numWeightCols)} 108px`;

  const renderTableTops = () => (
    <Box>
      <TabHeader
        subtitle="Table top size table"
        rightToggles={
          <PillToggle
            value={tt.mode}
            options={[{ value: 'single', label: 'Single weight' }, { value: 'multi', label: 'Multi-weight' }]}
            onChange={setTtMode}
          />
        }
      />

      <BigTitleInput value={tt.title} onChange={(v) => updateTableTops({ title: v })} />

      {tt.mode === 'multi' && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2.5, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Weight columns:
          </Typography>
          {ttWeightTitles.map((t, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 99,
                pl: 2,
                pr: 0.5,
                py: 0.25,
                gap: 0.5,
              }}
            >
              <input
                value={t}
                onChange={(e) => renameWeightCol(i, e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  font: 'inherit',
                  fontSize: '0.95rem',
                  width: `${Math.max(t.length, 8)}ch`,
                }}
              />
              <IconButton size="small" onClick={() => removeWeightCol(i)} sx={{ p: 0.5 }}>
                <CloseRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {tt.mode === 'multi' && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={addWeightCol}
          sx={{ borderRadius: 99, textTransform: 'none', mb: 2 }}
        >
          Add
        </Button>
      )}

      {/* Header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: ttGridCols,
          gap: 1.5,
          alignItems: 'flex-end',
          bgcolor: 'grey.50',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          mt: tt.mode === 'multi' ? 0 : 2.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Size ({lengthUnit})</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Shape</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Size other ({lengthUnit})</Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Size extended ({lengthUnit})</Typography>
        {tt.mode === 'multi'
          ? ttWeightTitles.map((t, i) => (
              <Box key={i}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                  Weight ({weightUnit})
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>{t}</Typography>
              </Box>
            ))
          : <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Weight ({weightUnit})</Typography>}
        <Box />
      </Box>

      {/* Data rows */}
      {ttRows.map((row, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'grid',
            gridTemplateColumns: ttGridCols,
            gap: 1.5,
            alignItems: 'center',
            px: 2,
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            type="number"
            value={row.size ?? ''}
            placeholder="—"
            onChange={(e) => updateTtRow(idx, { size: e.target.value })}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            size="small"
            placeholder="Square / Round / Rectangle"
            value={row.shape || ''}
            onChange={(e) => updateTtRow(idx, { shape: e.target.value })}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            size="small"
            type="number"
            value={row.sizeOther ?? ''}
            placeholder="—"
            onChange={(e) => updateTtRow(idx, { sizeOther: e.target.value })}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            size="small"
            type="number"
            value={row.sizeExtended ?? ''}
            placeholder="—"
            onChange={(e) => updateTtRow(idx, { sizeExtended: e.target.value })}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          {(row.weights || []).map((w, wi) => (
            <TextField
              key={wi}
              size="small"
              type="number"
              value={w.value ?? ''}
              placeholder="—"
              onChange={(e) => updateTtRowWeight(idx, wi, e.target.value)}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              size="small"
              disabled={idx === 0}
              onClick={() => updateTableTops({ rows: moveInArray(ttRows, idx, idx - 1) })}
              title="Move up"
              sx={{ p: 0.25 }}
            >
              <ArrowUpwardRounded sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={idx === ttRows.length - 1}
              onClick={() => updateTableTops({ rows: moveInArray(ttRows, idx, idx + 1) })}
              title="Move down"
              sx={{ p: 0.25 }}
            >
              <ArrowDownwardRounded sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => removeTtRow(idx)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddRounded />}
        onClick={addTtRow}
        sx={{ borderRadius: 99, textTransform: 'none', mt: 2 }}
      >
        Add row
      </Button>
    </Box>
  );

  // ─── Table Bases tab ───
  const tb = dims.tableBases;
  const tbCategories = tb.categories || [];
  const tbVariants = tb.variants || [];
  // Custom repeatable fields: labels defined once here, filled in per variant
  // (like the Advanced tab's variant fields). Values live on v.custom[id].
  const tbCustomFields = tb.customFields || [];
  // Global (baseline) values for the custom fields. Each size inherits these
  // unless it sets its own override.
  const tbCustomGlobals = tb.customGlobals || {};
  const updateCustomGlobal = (id, val) =>
    updateTableBases({ customGlobals: { ...tbCustomGlobals, [id]: val } });

  const addCustomField = () => {
    const id = `cf_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;
    updateTableBases({ customFields: [...tbCustomFields, { id, label: '' }] });
  };
  const updateCustomField = (id, label) =>
    updateTableBases({ customFields: tbCustomFields.map((f) => (f.id === id ? { ...f, label } : f)) });
  const removeCustomField = (id) => {
    const { [id]: _dropGlobal, ...restGlobals } = tbCustomGlobals;
    updateTableBases({
      customFields: tbCustomFields.filter((f) => f.id !== id),
      customGlobals: restGlobals,
      // Drop the removed field's values from every variant.
      variants: tbVariants.map((v) => {
        if (!v.custom || !(id in v.custom)) return v;
        const { [id]: _drop, ...rest } = v.custom;
        return { ...v, custom: rest };
      }),
    });
  };

  const addCategory = (name) => {
    if (!name?.trim()) return;
    if (tbCategories.includes(name.trim())) return;
    updateTableBases({ categories: [...tbCategories, name.trim()] });
  };
  const removeCategory = (cat) => updateTableBases({ categories: tbCategories.filter((c) => c !== cat) });

  const addTbVariant = () =>
    updateTableBases({ variants: [...tbVariants, { name: '', category: '', fields: {}, maxTop: {}, notes: '' }] });
  const updateTbVariant = (idx, patch) =>
    updateTableBases({ variants: tbVariants.map((v, i) => (i === idx ? { ...v, ...patch } : v)) });
  const removeTbVariant = (idx) => updateTableBases({ variants: tbVariants.filter((_, i) => i !== idx) });

  const [newCat, setNewCat] = useState('');

  const renderTableBases = () => (
    <Box>
      <TabHeader subtitle="Table base" />

      <SectionLabel>Categories</SectionLabel>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
        {tbCategories.map((c) => (
          <Box
            key={c}
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'primary.light',
              bgcolor: 'rgba(108,99,255,0.06)',
              borderRadius: 99,
              pl: 2,
              pr: 0.5,
              py: 0.25,
              gap: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>{c}</Typography>
            <IconButton size="small" onClick={() => removeCategory(c)} sx={{ p: 0.5 }}>
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        <TextField
          size="small"
          placeholder="Add category…"
          value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              addCategory(newCat);
              setNewCat('');
            }
          }}
          InputProps={{ sx: { borderRadius: 99, fontSize: '0.9rem' } }}
          sx={{ width: 200 }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={() => { addCategory(newCat); setNewCat(''); }}
          sx={{ borderRadius: 99, textTransform: 'none' }}
        >
          Add
        </Button>
      </Box>

      <SectionLabel>Custom fields</SectionLabel>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Define your own fields once and set a global value. Each size repeats these
        fields — leave a size's field blank to inherit the global value, or fill it to override.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        {tbCustomFields.map((f) => (
          <Box
            key={f.id}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              border: '1px solid', borderColor: 'divider', borderRadius: 2, pl: 1, pr: 0.5, py: 0.25,
            }}
          >
            <TextField
              size="small"
              variant="standard"
              placeholder="Field name…"
              value={f.label}
              onChange={(e) => updateCustomField(f.id, e.target.value)}
              InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', fontWeight: 500, width: 130 } }}
            />
            <IconButton size="small" onClick={() => removeCustomField(f.id)} sx={{ p: 0.5 }}>
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ))}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={addCustomField}
          sx={{ borderRadius: 99, textTransform: 'none' }}
        >
          Add Field
        </Button>
      </Box>

      {tbCustomFields.filter((f) => (f.label || '').trim()).length > 0 && (
        <>
          <SectionLabel>Global values</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2.5, mb: 3 }}>
            {tbCustomFields.filter((f) => (f.label || '').trim()).map((f) => (
              <TextField
                key={f.id}
                size="small"
                label={f.label}
                value={tbCustomGlobals[f.id] || ''}
                onChange={(e) => updateCustomGlobal(f.id, e.target.value)}
                fullWidth
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            ))}
          </Box>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Variants</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={addTbVariant}
          sx={{ borderRadius: 99, textTransform: 'none' }}
        >
          Add Variant
        </Button>
      </Box>

      {tbVariants.length === 0 && (
        <Typography variant="body2" color="text.secondary">No variants yet.</Typography>
      )}

      {(() => {
        // Group variants under their category heading. Order = each defined
        // category (in order), then any ungrouped variants last. Empty
        // categories don't render. If no categories exist, it's a flat list.
        const hasGroups = tbCategories.length > 0;
        const order = [];
        for (const cat of [...tbCategories, '']) {
          tbVariants.forEach((v, idx) => {
            const vc = tbCategories.includes(v.category) ? v.category : '';
            if (vc === cat) order.push({ v, idx, cat });
          });
        }
        let lastCat;
        return order.map(({ v, idx, cat }) => {
          const showHeading = hasGroups && cat !== lastCat;
          lastCat = cat;
          return (
          <Box key={idx}>
            {showHeading && (
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: cat ? 'primary.main' : 'text.secondary', mt: 2, mb: 1 }}>
                {cat || 'Ungrouped'}
              </Typography>
            )}
            <Box
              {...dropZoneProps('tbVariants', idx, tbVariants, (next) => updateTableBases({ variants: next }))}
              sx={{
                mb: 2.5, border: '1px solid',
                borderColor: isDragOver('tbVariants', idx) ? 'primary.main' : 'divider',
                borderRadius: 2, p: 2.5, opacity: isDragging('tbVariants', idx) ? 0.5 : 1,
                transition: 'border-color 0.12s, opacity 0.12s',
              }}
            >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2.5 }}>
            <Box
              {...dragHandleProps('tbVariants', idx)}
              sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
              title="Drag to reorder"
            >
              <DragIndicatorRounded sx={{ fontSize: 20 }} />
            </Box>
            <TextField
              size="small"
              placeholder='e.g. 16 Inch'
              value={v.name}
              onChange={(e) => updateTbVariant(idx, { name: e.target.value })}
              fullWidth
              InputProps={{ sx: { borderRadius: 2, fontSize: '1.05rem', fontWeight: 600 } }}
            />
            {/* Group this base with others of the same size via a category. */}
            <TextField
              select
              size="small"
              label="Group"
              value={tbCategories.includes(v.category) ? v.category : ''}
              onChange={(e) => updateTbVariant(idx, { category: e.target.value })}
              sx={{ minWidth: 150 }}
              InputProps={{ sx: { borderRadius: 2 } }}
            >
              <MenuItem value=""><em>No group</em></MenuItem>
              {tbCategories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <IconButton
              onClick={() => removeTbVariant(idx)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>

          <SectionLabel>Base dimensions</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2.5, mb: 3 }}>
            {TABLE_BASE_FIELDS.map((f) => {
              if (f.kind === 'pair') {
                return (
                  <Box key={f.keys.join('+')} sx={{ gridColumn: 'span 2' }}>
                    <PairUnitField
                      label={f.label}
                      values={[v.fields?.[f.keys[0]], v.fields?.[f.keys[1]]]}
                      onChange={(i, val) => updateTbVariant(idx, { fields: { ...v.fields, [f.keys[i]]: val } })}
                      unit={lengthUnit}
                    />
                  </Box>
                );
              }
              return (
                <UnitField
                  key={f.key}
                  label={f.label}
                  value={v.fields?.[f.key]}
                  onChange={(val) => updateTbVariant(idx, { fields: { ...v.fields, [f.key]: val } })}
                  unit={f.isWeight ? weightUnit : lengthUnit}
                />
              );
            })}
          </Box>

          <SectionLabel>Max top size</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2.5, mb: 3 }}>
            {MAX_TOP_FIELDS.map((f) => {
              if (f.kind === 'pair') {
                return (
                  <Box key={f.keys.join('+')} sx={{ gridColumn: 'span 2' }}>
                    <PairUnitField
                      label={f.label}
                      values={[v.maxTop?.[f.keys[0]], v.maxTop?.[f.keys[1]]]}
                      onChange={(i, val) => updateTbVariant(idx, { maxTop: { ...v.maxTop, [f.keys[i]]: val } })}
                      unit={lengthUnit}
                    />
                  </Box>
                );
              }
              return (
                <UnitField
                  key={f.key}
                  label={f.label}
                  value={v.maxTop?.[f.key]}
                  onChange={(val) => updateTbVariant(idx, { maxTop: { ...v.maxTop, [f.key]: val } })}
                  unit={lengthUnit}
                />
              );
            })}
          </Box>

          {tbCustomFields.filter((f) => (f.label || '').trim()).length > 0 && (
            <>
              <SectionLabel>Custom fields (override)</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2.5, mb: 3 }}>
                {tbCustomFields.filter((f) => (f.label || '').trim()).map((f) => (
                  <TextField
                    key={f.id}
                    size="small"
                    label={f.label}
                    value={v.custom?.[f.id] || ''}
                    onChange={(e) => updateTbVariant(idx, { custom: { ...v.custom, [f.id]: e.target.value } })}
                    placeholder={tbCustomGlobals[f.id] ? `${tbCustomGlobals[f.id]} (global)` : 'Value'}
                    fullWidth
                    InputProps={{ sx: { borderRadius: 2 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                ))}
              </Box>
            </>
          )}

          <SectionLabel>Notes</SectionLabel>
          <TextField
            size="small"
            multiline
            rows={3}
            placeholder="Optional notes…"
            value={v.notes || ''}
            onChange={(e) => updateTbVariant(idx, { notes: e.target.value })}
            fullWidth
            InputProps={{ sx: { borderRadius: 2 } }}
          />
            </Box>
          </Box>
          );
        });
      })()}
    </Box>
  );

  return (
    <Box>
      <Tabs
        value={dims.activeTab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          minHeight: 'auto',
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.95rem',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mr: 1,
            minHeight: 40,
            py: 1,
            px: 2.5,
            color: 'text.primary',
            '&.Mui-selected': {
              borderColor: 'text.primary',
              fontWeight: 600,
              color: 'text.primary',
            },
          },
        }}
      >
        <Tab value="simple" label="Simple" />
        <Tab value="advanced" label="Advanced" />
        <Tab value="tableTops" label="Table Tops" />
        <Tab value="tableBases" label="Table Bases" />
      </Tabs>

      {dims.activeTab === 'simple' && renderSimple()}
      {dims.activeTab === 'advanced' && renderAdvanced()}
      {dims.activeTab === 'tableTops' && renderTableTops()}
      {dims.activeTab === 'tableBases' && renderTableBases()}
    </Box>
  );
};

export default DimensionsEditor;
